import { API, BACKEND_URL } from '@/config/api';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Brain, TrendingUp, TrendingDown, Minus, AlertTriangle, 
  CheckCircle, Loader2, RefreshCw, ChevronDown, ChevronUp,
  Activity, Target, Zap, Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const RISK_COLORS = {
  low: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', badge: 'bg-green-500' },
  moderate: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200', badge: 'bg-yellow-500' },
  elevated: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', badge: 'bg-orange-500' },
  high: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', badge: 'bg-red-500' },
  critical: { bg: 'bg-red-200', text: 'text-red-900', border: 'border-red-300', badge: 'bg-red-900' },
  unknown: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200', badge: 'bg-gray-500' }
};

const SEASON_INFO = {
  spring: { label: 'Vår', icon: '🌱', risk: 'Høy risiko' },
  summer: { label: 'Sommer', icon: '☀️', risk: 'Moderat-høy risiko' },
  autumn: { label: 'Høst', icon: '🍂', risk: 'Moderat risiko' },
  winter: { label: 'Vinter', icon: '❄️', risk: 'Lav risiko' }
};

export default function MLPredictionPanel({ selectedAreaId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [mlOverview, setMlOverview] = useState(null);
  const [areaPreduction, setAreaPrediction] = useState(null);
  const [predictionLoading, setPredictionLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ trend: true, risk: true, prediction: false });

  // Fetch ML overview
  const fetchMlOverview = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/satellite/ml/overview`, { withCredentials: true });
      if (response.data.success) {
        setMlOverview(response.data);
      }
    } catch (err) {
      console.error('Error fetching ML overview:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch prediction for specific area
  const fetchAreaPrediction = useCallback(async (areaId) => {
    if (!areaId) return;
    
    try {
      setPredictionLoading(true);
      const response = await axios.get(`${API}/satellite/ml/predict/${areaId}`, {
        params: { days_ahead: 7 },
        withCredentials: true
      });
      if (response.data.success) {
        setAreaPrediction(response.data);
      }
    } catch (err) {
      console.error('Error fetching area prediction:', err);
    } finally {
      setPredictionLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMlOverview();
  }, [fetchMlOverview]);

  useEffect(() => {
    if (selectedAreaId) {
      fetchAreaPrediction(selectedAreaId);
    }
  }, [selectedAreaId, fetchAreaPrediction]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getTrendIcon = (trend) => {
    if (trend === 'increasing' || trend === 'rapidly_increasing') {
      return <TrendingUp className="w-4 h-4 text-red-500" />;
    }
    if (trend === 'decreasing' || trend === 'rapidly_decreasing') {
      return <TrendingDown className="w-4 h-4 text-green-500" />;
    }
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  const getTrendLabel = (trend) => {
    const labels = {
      'rapidly_increasing': 'Raskt økende',
      'increasing': 'Økende',
      'stable': 'Stabil',
      'decreasing': 'Synkende',
      'rapidly_decreasing': 'Raskt synkende',
      'unknown': 'Ukjent'
    };
    return labels[trend] || trend;
  };

  if (loading) {
    return (
      <Card className="bg-white/90 backdrop-blur">
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            <span className="ml-3 text-slate-600">Laster ML-analyse...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Brain className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <span className="text-slate-900">ML Prediksjon</span>
              <p className="text-xs font-normal text-slate-500">
                Maskinlæringsbasert risikovurdering
              </p>
            </div>
          </CardTitle>
          {mlOverview?.season && (
            <Badge variant="outline" className="bg-white">
              {SEASON_INFO[mlOverview.season]?.icon} {SEASON_INFO[mlOverview.season]?.label}
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Season Risk Info */}
        {mlOverview?.season && (
          <div className="p-3 bg-white/50 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" />
              <span className="text-sm text-slate-600">Sesongrisiko:</span>
            </div>
            <span className="text-sm font-medium text-slate-700">
              {SEASON_INFO[mlOverview.season]?.risk}
            </span>
          </div>
        )}

        {/* Overview Summary */}
        {mlOverview?.summary && (
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-white rounded-lg text-center">
              <p className="text-2xl font-bold text-purple-600">
                {mlOverview.summary.areas_with_data}
              </p>
              <p className="text-xs text-slate-500">Områder analysert</p>
            </div>
            <div className="p-3 bg-white rounded-lg text-center">
              <p className="text-2xl font-bold text-orange-600">
                {mlOverview.summary.high_risk_predictions}
              </p>
              <p className="text-xs text-slate-500">Høyrisiko-prediksjoner</p>
            </div>
          </div>
        )}

        {/* Area-specific prediction */}
        {selectedAreaId && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-slate-700 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Prediksjon for {selectedAreaId}
              </h4>
              {predictionLoading && <Loader2 className="w-4 h-4 animate-spin text-purple-600" />}
            </div>

            {areaPreduction && !predictionLoading && (
              <div className="space-y-3">
                {/* Trend Analysis */}
                <button 
                  onClick={() => toggleSection('trend')}
                  className="w-full p-3 bg-white rounded-lg text-left hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-blue-500" />
                      <span className="font-medium text-slate-700">Trendanalyse</span>
                    </div>
                    {expandedSections.trend ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                  
                  {expandedSections.trend && areaPreduction.trend_analysis && (
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-500">Trend:</span>
                        <div className="flex items-center gap-1">
                          {getTrendIcon(areaPreduction.trend_analysis.trend)}
                          <span className="text-sm font-medium">
                            {getTrendLabel(areaPreduction.trend_analysis.trend)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-500">Endring:</span>
                        <span className={`text-sm font-medium ${
                          areaPreduction.trend_analysis.change_percent > 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {areaPreduction.trend_analysis.change_percent > 0 ? '+' : ''}
                          {areaPreduction.trend_analysis.change_percent?.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-500">Nåværende:</span>
                        <span className="text-sm font-medium">
                          {areaPreduction.trend_analysis.current_value?.toFixed(3)} mg/m³
                        </span>
                      </div>
                    </div>
                  )}
                </button>

                {/* Bloom Risk */}
                {areaPreduction.bloom_risk && (
                  <button 
                    onClick={() => toggleSection('risk')}
                    className="w-full p-3 bg-white rounded-lg text-left hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-orange-500" />
                        <span className="font-medium text-slate-700">Oppblomstringsrisiko</span>
                      </div>
                      {expandedSections.risk ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                    
                    {expandedSections.risk && (
                      <div className="mt-3 space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-500">Sannsynlighet</span>
                            <span className="font-medium">
                              {(areaPreduction.bloom_risk.bloom_probability * 100).toFixed(0)}%
                            </span>
                          </div>
                          <Progress 
                            value={areaPreduction.bloom_risk.bloom_probability * 100} 
                            className="h-2"
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-500">Risikokategori:</span>
                          <Badge className={RISK_COLORS[areaPreduction.bloom_risk.risk_category]?.badge + ' text-white'}>
                            {areaPreduction.bloom_risk.risk_category === 'low' && 'Lav'}
                            {areaPreduction.bloom_risk.risk_category === 'moderate' && 'Moderat'}
                            {areaPreduction.bloom_risk.risk_category === 'elevated' && 'Forhøyet'}
                            {areaPreduction.bloom_risk.risk_category === 'high' && 'Høy'}
                            {areaPreduction.bloom_risk.risk_category === 'critical' && 'Kritisk'}
                          </Badge>
                        </div>
                        
                        <div className="p-2 bg-slate-50 rounded text-xs text-slate-600">
                          {areaPreduction.bloom_risk.recommendation}
                        </div>
                      </div>
                    )}
                  </button>
                )}

                {/* Predictions */}
                {areaPreduction.predictions?.predictions?.length > 0 && (
                  <button 
                    onClick={() => toggleSection('prediction')}
                    className="w-full p-3 bg-white rounded-lg text-left hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-purple-500" />
                        <span className="font-medium text-slate-700">7-dagers prediksjon</span>
                      </div>
                      {expandedSections.prediction ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                    
                    {expandedSections.prediction && (
                      <div className="mt-3 space-y-2">
                        {areaPreduction.predictions.predictions.slice(0, 5).map((pred, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <span className="text-slate-500">{pred.date}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono">{pred.predicted_value.toFixed(3)}</span>
                              <Badge variant="outline" className={
                                pred.risk_level === 'low' ? 'border-green-300 text-green-700' :
                                pred.risk_level === 'moderate' ? 'border-yellow-300 text-yellow-700' :
                                'border-red-300 text-red-700'
                              }>
                                {pred.risk_level === 'low' ? 'Lav' : 
                                 pred.risk_level === 'moderate' ? 'Mod' : 'Høy'}
                              </Badge>
                            </div>
                          </div>
                        ))}
                        <p className="text-xs text-slate-400 mt-2">
                          Konfidensintervall: {(areaPreduction.predictions.confidence * 100).toFixed(0)}%
                        </p>
                      </div>
                    )}
                  </button>
                )}

                {/* Anomaly Detection */}
                {areaPreduction.anomaly_detection && (
                  <div className={`p-3 rounded-lg ${
                    areaPreduction.anomaly_detection.is_anomaly 
                      ? 'bg-orange-50 border border-orange-200' 
                      : 'bg-green-50 border border-green-200'
                  }`}>
                    <div className="flex items-center gap-2">
                      {areaPreduction.anomaly_detection.is_anomaly ? (
                        <AlertTriangle className="w-4 h-4 text-orange-600" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      )}
                      <span className={`text-sm font-medium ${
                        areaPreduction.anomaly_detection.is_anomaly ? 'text-orange-700' : 'text-green-700'
                      }`}>
                        {areaPreduction.anomaly_detection.message}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!areaPreduction && !predictionLoading && (
              <div className="text-center py-4 text-slate-500">
                <p className="text-sm">Klikk på et område med data for prediksjon</p>
              </div>
            )}
          </div>
        )}

        {/* ML Areas List */}
        {mlOverview?.areas && mlOverview.areas.filter(a => a.bloom_probability !== null).length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-slate-700">Områder sortert etter risiko</h4>
            {mlOverview.areas
              .filter(a => a.bloom_probability !== null)
              .slice(0, 5)
              .map((area, i) => {
                const riskColors = RISK_COLORS[area.risk_category] || RISK_COLORS.unknown;
                return (
                  <div 
                    key={area.area_id}
                    className={`p-2 rounded-lg ${riskColors.bg} ${riskColors.border} border cursor-pointer hover:opacity-80`}
                    onClick={() => fetchAreaPrediction(area.area_id)}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${riskColors.text}`}>
                        {area.area_id} - {area.area_name}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">
                          {(area.bloom_probability * 100).toFixed(0)}%
                        </span>
                        <Badge className={`${riskColors.badge} text-white text-xs`}>
                          {area.risk_category === 'low' ? 'Lav' : 
                           area.risk_category === 'moderate' ? 'Moderat' : 'Høy'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
